import {
  PlacementXAnchor,
  PlacementYAnchor,
  TextAlignment,
  TextVerticalAlignment,
} from "building-types/annotation/annotationTypesEnum";
import type { LoadDescriptor } from "building-types/electrical/electricalTypes";
import { LoadSymbol, LoadType } from "building-types/electrical/electricalTypesEnum";
import {
  HUNDRETH_INCH_PER_SVG_UNIT,
  SVG_UNITS_PER_100TH_INCH,
  SVG_UNITS_TO_INCHES,
} from "common/constants/pdfSettings";
import type { SheetInches } from "common/types/nominalNumbersTypes";
import { degreeToRadian, radianToDegree } from "common/utils/math";
import { numMul, sheetInchesNew } from "common/utils/nominalNumbers";
import { textAlignmentToXAnchor } from "common/utils/textAlignment";
import { defaultLoadCatalog } from "doc/elements/electrical/electricalTools";
import { DIST_EPSILON } from "geom/core";
import {
  mat3Dot,
  mat3ExtractRotationAngleFrom,
  mat3ExtractScaleVectorFrom,
  mat3Mult,
  mat3Reduce,
  mat3Scale,
  mat3CSSRotate as rotate,
  mat3Translate as translate,
} from "geom/mat3";
import type { Mat3 } from "geom/mat3";
import type { Vec2 } from "geom/vec2";
import { vec2AngleBetweenCW, vec2Normalize } from "geom/vec2";
import { jsxToString } from "lib/house/annotation/conversion";
import { withSwitchedOutletFillInLoadSvg } from "lib/house/annotation/electrical/switchedOutletSheetGeometry";
import PlanLabel from "lib/house/annotation/PlanLabel";
import type { BaseAnnoProps, BaseStyleProps } from "lib/house/annotation/svgSettings";
import { baseAnnoDefault, baseStyleDefault } from "lib/house/annotation/svgSettings";
import type { JSX } from "react";

interface LoadOnlyProps extends BaseAnnoProps {
  loadDescriptor: Pick<LoadDescriptor, "symbol" | "planLabel">;
  normal: Vec2;
  levelId: string;
  floorIndex: number;
  posFromController?: boolean;
  planRotationRads?: number;
  svgTextScale?: number;
  /**
   * Places round ceiling-light labels via loadPlanLabelYOffset (direction-aware, like WallLight)
   * instead of their fixed per-symbol offset. Set only on the live-plans sheet path where Overlap
   * Reduction actually runs; the Studio editor and the built-in electrical page keep the fixed one.
   */
  reduceOverlapLoadLabels?: boolean;
}

export type LoadProps = LoadOnlyProps &
  Pick<
    BaseStyleProps,
    | "fillLightPrimary"
    | "highlightStrokeWidth"
    | "fillDarkPrimary"
    | "strokeDarkPrimary"
    | "smallStrokeWidth"
    | "strokeDarkPrimary"
    | "extraSmallStrokeWidth"
    | "regularStrokeWidth"
    | "textStrokeWidth"
    | "textFillPrimary"
    | "textStrokePrimary"
    | "regularFontSize"
    | "smallFontSize"
    | "fontFamily0"
  >;

export const loadDefault: Required<LoadProps> & Required<BaseAnnoProps> = {
  ...baseStyleDefault,
  ...baseAnnoDefault,
  loadDescriptor: defaultLoadCatalog[LoadType.RECESSED_CEILING_LIGHT],
  normal: [0, 1],
  levelId: "",
  floorIndex: 0,
  posFromController: false,
  planRotationRads: 0,
  svgTextScale: 1,
  reduceOverlapLoadLabels: false,
};

interface GenerateLoadOptions {
  switchedOutletFillColor?: string;
}

export function generateLoad(props?: Partial<LoadProps> & GenerateLoadOptions, wrapSvg = false): string {
  const { switchedOutletFillColor, ...loadProps } = props ?? {};
  const svg = jsxToString(<Load {...loadDefault} {...loadProps} />, wrapSvg);
  if (switchedOutletFillColor !== undefined && loadProps.loadDescriptor?.symbol === LoadSymbol.OUTLET_SWITCHED) {
    return withSwitchedOutletFillInLoadSvg(svg, switchedOutletFillColor);
  }
  return svg;
}

const Load = (props: Required<LoadProps>) => {
  const { loadDescriptor, x, y } = props;
  const ResolvedLoadType = loadSymbolSelector[loadDescriptor.symbol];
  const [rawX, rawY] = props.normal;
  const [oX, oY] = vec2Normalize(rawX, rawY);
  const angleCW = radianToDegree(vec2AngleBetweenCW(0, 1, oX, oY));
  return <g transform={`rotate(${angleCW} ${x},${y})`}>{ResolvedLoadType ? <ResolvedLoadType {...props} /> : null}</g>;
};

export default Load;

/** Clearing the "Reported Name" field commits "" rather than deleting it, and a name typed as spaces
 * draws no glyphs — both must read as no label, not a phantom one that still anchors a leader line. */
export function hasPlanLabel(planLabel: string | undefined): planLabel is string {
  return planLabel !== undefined && planLabel.trim() !== "";
}

const CAN_LIGHT_OUTER_RADIUS = 6;
const CAN_LIGHT_INNER_RADIUS = 4;

interface RoundCeilingLightPlanLabelProps {
  /** The symbol component's own render props, passed through whole. */
  loadProps: Required<LoadProps>;
  /** What loadPlanLabelYOffset measures the gap from, as OUTLET_LINE_BOTTOM does for outlets. */
  symbolExtentY: number;
  /** Pre-Overlap-Reduction offset and anchoring, kept verbatim so those paths stay pixel-identical. */
  legacyOffset: Vec2;
  legacyAlignment?: TextAlignment;
  legacyVerticalAlignment?: TextVerticalAlignment;
}

/**
 * Plan label for the concentric-circle ceiling lights, the symbols ROUND_CEILING_LIGHT_SYMBOLS
 * (electricalIndependentLoadsFromRoom.ts) gives a steerable `normal`. Under Overlap Reduction the
 * anchoring has to switch along with the offset: loadPlanLabelYOffset positions the text's TOP edge
 * and labelClearanceSymbolUnits corrects a CENTER-anchored half-width, the pair OutletPlanLabel and
 * MountedWallLight already use.
 *
 * findPlanLabelInElement reaches the PlanLabel inside by *calling* this component — it walks the
 * tree with no React renderer. So this has to stay hook-free and has to return the element itself
 * rather than rendered markup, or the sheet and editor placement helpers go null and the label
 * silently vanishes from plans.
 */
function RoundCeilingLightPlanLabel(props: RoundCeilingLightPlanLabelProps) {
  const { loadProps, symbolExtentY, legacyOffset, legacyAlignment, legacyVerticalAlignment } = props;
  const planLabel = loadProps.loadDescriptor.planLabel;
  if (!hasPlanLabel(planLabel)) {
    return null;
  }

  const inverseScale = 1 / (loadProps.scale * SVG_UNITS_PER_100TH_INCH);
  const angleCW = keepTextUprightAngle(loadProps);
  const reduceOverlap = loadProps.reduceOverlapLoadLabels;
  // A transform list applies right-to-left, so where `rotate` sits decides whether the offset turns
  // with the symbol: the legacy chain spins its fixed screen offset, while translate listed first
  // (so applied last) leaves the Overlap Reduction offset along local +Y — the frame
  // loadPlanLabelYOffset measures in.
  const transform = reduceOverlap
    ? [
        translate(0, loadPlanLabelYOffset(loadProps, symbolExtentY, planLabel)),
        mat3Scale(inverseScale, inverseScale),
        rotate(-angleCW),
      ]
    : [rotate(-angleCW), translate(legacyOffset[0], legacyOffset[1]), mat3Scale(inverseScale, inverseScale)];

  return (
    <PlanLabel
      text={planLabel}
      transform={transform}
      fill={loadProps.textFillPrimary}
      fontSize={numMul(loadProps.smallFontSize, loadProps.svgTextScale)}
      fontFamily={loadProps.fontFamily0}
      alignment={reduceOverlap ? TextAlignment.CENTER : legacyAlignment}
      verticalAlignment={reduceOverlap ? TextVerticalAlignment.TOP : legacyVerticalAlignment}
      stroke={loadProps.textStrokePrimary}
      strokeWidth={loadProps.textStrokeWidth}
    />
  );
}

const CIRCLE_4_SYMBOL_EXTENT_Y = 4;

const Circle4 = (props: Required<LoadProps>) => {
  const scale = props.scale * SVG_UNITS_PER_100TH_INCH;
  return (
    <g transform={`translate(${props.x} ,${props.y}) scale(${scale},${scale})`}>
      <ellipse
        cx="0"
        cy="0"
        rx="4"
        ry="4"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.smallStrokeWidth}
      />
      <RoundCeilingLightPlanLabel
        loadProps={props}
        symbolExtentY={CIRCLE_4_SYMBOL_EXTENT_Y}
        legacyOffset={[7, 5]}
        legacyAlignment={TextAlignment.LEFT}
      />
    </g>
  );
};

const CIRCLE_7_SYMBOL_EXTENT_Y = 7;

const Circle7 = (props: Required<LoadProps>) => {
  const scale = props.scale * SVG_UNITS_PER_100TH_INCH;
  return (
    <g transform={`translate(${props.x} ,${props.y}) scale(${scale},${scale})`}>
      <ellipse
        cx="0"
        cy="0"
        rx="7"
        ry="7"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.smallStrokeWidth}
      />
      <RoundCeilingLightPlanLabel
        loadProps={props}
        symbolExtentY={CIRCLE_7_SYMBOL_EXTENT_Y}
        legacyOffset={[0, 3.5]}
        legacyAlignment={TextAlignment.CENTER}
      />
    </g>
  );
};

const CIRCLE_8_SYMBOL_EXTENT_Y = 8;

const Circle8 = (props: Required<LoadProps>) => {
  const scale = props.scale * SVG_UNITS_PER_100TH_INCH;
  return (
    <g transform={`translate(${props.x} ,${props.y}) scale(${scale},${scale})`}>
      <ellipse
        cx="0"
        cy="0"
        rx="8"
        ry="8"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.smallStrokeWidth}
      />
      <RoundCeilingLightPlanLabel
        loadProps={props}
        symbolExtentY={CIRCLE_8_SYMBOL_EXTENT_Y}
        legacyOffset={[0, 3.75]}
        legacyAlignment={TextAlignment.CENTER}
      />
    </g>
  );
};

const CIRCLE_DL_45_15_SYMBOL_EXTENT_Y = 4.5;

const CircleDL4515 = (props: Required<LoadProps>) => {
  const scale = props.scale * SVG_UNITS_PER_100TH_INCH;
  return (
    <g transform={`translate(${props.x} ,${props.y}) scale(${scale},${scale})`}>
      <ellipse
        cx="0"
        cy="0"
        rx="4.5"
        ry="4.5"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.smallStrokeWidth}
      />
      <ellipse
        cx="0"
        cy="0"
        rx="1.5"
        ry="1.5"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.smallStrokeWidth}
      />
      <RoundCeilingLightPlanLabel
        loadProps={props}
        symbolExtentY={CIRCLE_DL_45_15_SYMBOL_EXTENT_Y}
        legacyOffset={[7, 4.5]}
      />
    </g>
  );
};

const CIRCLE_DL_60_40_SYMBOL_EXTENT_Y = CAN_LIGHT_OUTER_RADIUS;

const CircleDL6040 = (props: Required<LoadProps>) => {
  const scale = props.scale * SVG_UNITS_PER_100TH_INCH;
  return (
    <g transform={`translate(${props.x} ,${props.y}) scale(${scale},${scale})`}>
      <ellipse
        cx="0"
        cy="0"
        rx={CAN_LIGHT_OUTER_RADIUS}
        ry={CAN_LIGHT_OUTER_RADIUS}
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.smallStrokeWidth}
      />
      <ellipse
        cx="0"
        cy="0"
        rx={CAN_LIGHT_INNER_RADIUS}
        ry={CAN_LIGHT_INNER_RADIUS}
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.extraSmallStrokeWidth}
      />
      <RoundCeilingLightPlanLabel
        loadProps={props}
        symbolExtentY={CIRCLE_DL_60_40_SYMBOL_EXTENT_Y}
        legacyOffset={[9, 5]}
        legacyAlignment={TextAlignment.CENTER}
        legacyVerticalAlignment={TextVerticalAlignment.TOP}
      />
    </g>
  );
};

const FramedCircle = (props: Required<LoadProps>) => {
  const planLabel = props.loadDescriptor.planLabel;
  const scale = props.scale * SVG_UNITS_PER_100TH_INCH;
  const inverseScale = 1 / scale;
  const angleCW = keepTextUprightAngle(props);
  return (
    <g transform={`translate(${props.x} ,${props.y}) scale(${scale},${scale})`}>
      <rect
        x="-4"
        y="-4"
        width="8"
        height="8"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.smallStrokeWidth}
      />
      <ellipse
        cx="0"
        cy="0"
        rx="4"
        ry="4"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.smallStrokeWidth}
      />
      {hasPlanLabel(planLabel) && (
        <PlanLabel
          text={planLabel}
          transform={[rotate(-angleCW), translate(7, 5), mat3Scale(inverseScale, inverseScale)]}
          fill={props.textFillPrimary}
          fontSize={numMul(props.smallFontSize, props.svgTextScale)}
          fontFamily={props.fontFamily0}
          alignment={TextAlignment.LEFT}
          stroke={props.textStrokePrimary}
          strokeWidth={props.textStrokeWidth}
        />
      )}
    </g>
  );
};

const CIRCLE_DL_12_SYMBOL_EXTENT_Y = 12;

const CircleDL12 = (props: Required<LoadProps>) => {
  const scale = props.scale * SVG_UNITS_PER_100TH_INCH;
  return (
    <g transform={`translate(${props.x} ,${props.y}) scale(${scale},${scale})`}>
      <ellipse
        cx="0"
        cy="0"
        rx="12"
        ry="12"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.smallStrokeWidth}
      />
      <ellipse
        cx="0"
        cy="0"
        rx="10"
        ry="10"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.extraSmallStrokeWidth}
      />
      <RoundCeilingLightPlanLabel
        loadProps={props}
        symbolExtentY={CIRCLE_DL_12_SYMBOL_EXTENT_Y}
        legacyOffset={[13, 11]}
        legacyAlignment={TextAlignment.LEFT}
      />
    </g>
  );
};

const RectangleDL2X4 = (props: Required<LoadProps>) => {
  const planLabel = props.loadDescriptor.planLabel;
  const scale = props.scale * SVG_UNITS_PER_100TH_INCH;
  const inverseScale = 1 / scale;
  const angleCW = keepTextUprightAngle(props);
  return (
    <g transform={`translate(${props.x} ,${props.y}) scale(${scale},${scale})  rotate(90)`}>
      <rect
        x="-48"
        y="-24"
        width="96"
        height="48"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.extraSmallStrokeWidth}
      />
      <rect
        x="-44"
        y="-20"
        width="88"
        height="40"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.extraSmallStrokeWidth}
      />
      {hasPlanLabel(planLabel) && (
        <PlanLabel
          text={planLabel}
          transform={[
            rotate(angleCW === 0 || angleCW === 180 ? -angleCW + 180 : -angleCW - 90),
            translate(0, 4),
            mat3Scale(inverseScale, inverseScale),
          ]}
          fill={props.textFillPrimary}
          fontSize={numMul(props.smallFontSize, props.svgTextScale)}
          fontFamily={props.fontFamily0}
          alignment={TextAlignment.CENTER}
          stroke={props.textStrokePrimary}
          strokeWidth={props.textStrokeWidth}
        />
      )}
    </g>
  );
};

const RectangleDL1X4 = (props: Required<LoadProps>) => {
  const planLabel = props.loadDescriptor.planLabel;
  const scale = props.scale * SVG_UNITS_PER_100TH_INCH;
  const inverseScale = 1 / scale;
  const angleCW = keepTextUprightAngle(props);
  return (
    <g transform={`translate(${props.x} ,${props.y}) scale(${scale},${scale})  rotate(90)`}>
      <rect
        x="-48"
        y="-12"
        width="96"
        height="24"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.extraSmallStrokeWidth}
      />
      <rect
        x="-44"
        y="-8"
        width="88"
        height="16"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.extraSmallStrokeWidth}
      />
      {hasPlanLabel(planLabel) && (
        <PlanLabel
          text={planLabel}
          transform={[
            rotate(angleCW === 0 || angleCW === 180 ? -angleCW + 180 : -angleCW - 90),
            translate(0, 4),
            mat3Scale(inverseScale, inverseScale),
          ]}
          fill={props.textFillPrimary}
          fontSize={numMul(props.smallFontSize, props.svgTextScale)}
          fontFamily={props.fontFamily0}
          alignment={TextAlignment.CENTER}
          stroke={props.textStrokePrimary}
          strokeWidth={props.textStrokeWidth}
        />
      )}
    </g>
  );
};

const RectangleDL24X4 = (props: Required<LoadProps>) => {
  const planLabel = props.loadDescriptor.planLabel;
  const scale = props.scale * SVG_UNITS_PER_100TH_INCH;
  const inverseScale = 1 / scale;
  const angleCW = keepTextUprightAngle(props);
  return (
    <g transform={`translate(${props.x} ,${props.y}) scale(${scale},${scale})  rotate(90)`}>
      <rect
        x="-24"
        y="-4"
        width="48"
        height="8"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.extraSmallStrokeWidth}
      />
      <rect
        x="-20"
        y="-3"
        width="40"
        height="6"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.extraSmallStrokeWidth}
      />
      {hasPlanLabel(planLabel) && (
        <PlanLabel
          text={planLabel}
          transform={[
            rotate(angleCW === 0 || angleCW === 180 ? -angleCW + 180 : -angleCW - 90),
            translate(0, 4),
            mat3Scale(inverseScale, inverseScale),
          ]}
          fill={props.textFillPrimary}
          fontSize={numMul(props.smallFontSize, props.svgTextScale)}
          fontFamily={props.fontFamily0}
          alignment={TextAlignment.CENTER}
          stroke={props.textStrokePrimary}
          strokeWidth={props.textStrokeWidth}
        />
      )}
    </g>
  );
};

const CeilingLight = (props: Required<LoadProps>) => {
  const planLabel = props.loadDescriptor.planLabel;
  const scale = props.scale * SVG_UNITS_PER_100TH_INCH;
  const inverseScale = 1 / scale;
  const angleCW = keepTextUprightAngle(props);
  return (
    <g transform={`translate(${props.x} ,${props.y}) scale(${scale},${scale})`}>
      <ellipse
        cx="0"
        cy="0"
        rx="5"
        ry="5"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.regularStrokeWidth}
      />
      <line x1="0" y1="5" x2="0" y2="9" stroke={props.strokeDarkPrimary} strokeWidth={props.regularStrokeWidth} />
      <line x1="0" y1="-5" x2="0" y2="-9" stroke={props.strokeDarkPrimary} strokeWidth={props.regularStrokeWidth} />
      <line x1="5" y1="0" x2="9" y2="0" stroke={props.strokeDarkPrimary} strokeWidth={props.regularStrokeWidth} />
      <line x1="-5" y1="0" x2="-9" y2="0" stroke={props.strokeDarkPrimary} strokeWidth={props.regularStrokeWidth} />
      {hasPlanLabel(planLabel) && (
        <PlanLabel
          text={planLabel}
          transform={[rotate(-angleCW), translate(7, 5), mat3Scale(inverseScale, inverseScale)]}
          fill={props.textFillPrimary}
          fontSize={numMul(props.smallFontSize, props.svgTextScale)}
          fontFamily={props.fontFamily0}
          alignment={TextAlignment.LEFT}
          stroke={props.textStrokePrimary}
          strokeWidth={props.textStrokeWidth}
        />
      )}
    </g>
  );
};

/** Extent of the MountedWallLight/FloodLight symbols along local Y, matching OUTLET_LINE_BOTTOM's role for outlets. */
const WALL_LIGHT_SYMBOL_EXTENT_Y = 9;

const MountedWallLight = (props: Required<LoadProps>) => {
  const planLabel = props.loadDescriptor.planLabel;
  const scale = props.scale * SVG_UNITS_PER_100TH_INCH;
  const inverseScale = 1 / scale;
  const angleCW = keepTextUprightAngle(props);
  return (
    <g transform={`translate(${props.x} ,${props.y}) scale(${scale},${scale})`}>
      <ellipse
        cx="0"
        cy="0"
        rx="5"
        ry="5"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.regularStrokeWidth}
      />
      <line x1="0" y1="5" x2="0" y2="9" stroke={props.strokeDarkPrimary} strokeWidth={props.regularStrokeWidth} />
      {/* <line x1="0" y1="-5" x2="0" y2="-9" stroke={props.strokeDarkPrimary} strokeWidth={props.regularStrokeWidth} /> */}
      <line x1="5" y1="0" x2="9" y2="0" stroke={props.strokeDarkPrimary} strokeWidth={props.regularStrokeWidth} />
      <line x1="-5" y1="0" x2="-9" y2="0" stroke={props.strokeDarkPrimary} strokeWidth={props.regularStrokeWidth} />
      {hasPlanLabel(planLabel) && (
        <PlanLabel
          text={planLabel}
          verticalAlignment={TextVerticalAlignment.TOP}
          transform={[
            translate(0, loadPlanLabelYOffset(props, WALL_LIGHT_SYMBOL_EXTENT_Y, planLabel)),
            mat3Scale(inverseScale, inverseScale),
            rotate(-angleCW),
          ]}
          fill={props.textFillPrimary}
          fontSize={numMul(props.smallFontSize, props.svgTextScale)}
          fontFamily={props.fontFamily0}
          alignment={TextAlignment.CENTER}
          stroke={props.textStrokePrimary}
          strokeWidth={props.textStrokeWidth}
        />
      )}
    </g>
  );
};

const FloodLight = (props: Required<LoadProps>) => {
  const planLabel = props.loadDescriptor.planLabel;
  const scale = props.scale * SVG_UNITS_PER_100TH_INCH;
  const inverseScale = 1 / scale;
  const angleCW = keepTextUprightAngle(props);
  return (
    <g transform={`translate(${props.x} ,${props.y}) scale(${scale},${scale})`}>
      <ellipse
        cx="0"
        cy="0"
        rx="5"
        ry="5"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.regularStrokeWidth}
      />
      <line x1="5" y1="0" x2="10" y2="7" stroke={props.strokeDarkPrimary} strokeWidth={props.regularStrokeWidth} />
      <line x1="-5" y1="0" x2="-10" y2="7" stroke={props.strokeDarkPrimary} strokeWidth={props.regularStrokeWidth} />
      <line x1="3" y1="3" x2="6" y2="10" stroke={props.strokeDarkPrimary} strokeWidth={props.regularStrokeWidth} />
      <line x1="-3" y1="3" x2="-6" y2="10" stroke={props.strokeDarkPrimary} strokeWidth={props.regularStrokeWidth} />
      {hasPlanLabel(planLabel) && (
        <PlanLabel
          text={planLabel}
          verticalAlignment={TextVerticalAlignment.TOP}
          transform={[
            translate(0, loadPlanLabelYOffset(props, WALL_LIGHT_SYMBOL_EXTENT_Y, planLabel)),
            mat3Scale(inverseScale, inverseScale),
            rotate(-angleCW),
          ]}
          fill={props.textFillPrimary}
          fontSize={numMul(props.smallFontSize, props.svgTextScale)}
          fontFamily={props.fontFamily0}
          alignment={TextAlignment.CENTER}
          stroke={props.textStrokePrimary}
          strokeWidth={props.textStrokeWidth}
        />
      )}
    </g>
  );
};

/** Extent of the TrackCeilingLight symbol along local Y, matching OUTLET_LINE_BOTTOM's role for outlets. */
const TRACK_CEILING_LIGHT_SYMBOL_EXTENT_Y = 6;

const TrackCeilingLight = (props: Required<LoadProps>) => {
  const planLabel = props.loadDescriptor.planLabel;
  const scale = props.scale * SVG_UNITS_PER_100TH_INCH;
  const inverseScale = 1 / scale;
  const angleCW = keepTextUprightAngle(props);
  return (
    <g transform={`translate(${props.x} ,${props.y}) scale(${scale},${scale})`}>
      <rect
        x="-24"
        y="-2"
        width="48"
        height="4"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.extraSmallStrokeWidth}
      />
      <g transform="translate(-16,0)">
        <g id="track_bulb">
          <ellipse
            cx="0"
            cy="3"
            rx="3"
            ry="3"
            fill={props.fillLightPrimary}
            stroke={props.strokeDarkPrimary}
            strokeWidth={props.extraSmallStrokeWidth}
          />
        </g>
      </g>
      <g transform="translate(0,0)">
        <g id="track_bulb">
          <ellipse
            cx="0"
            cy="3"
            rx="3"
            ry="3"
            fill={props.fillLightPrimary}
            stroke={props.strokeDarkPrimary}
            strokeWidth={props.extraSmallStrokeWidth}
          />
        </g>
      </g>
      <g transform="translate(16,0)">
        <g id="track_bulb">
          <ellipse
            cx="0"
            cy="3"
            rx="3"
            ry="3"
            fill={props.fillLightPrimary}
            stroke={props.strokeDarkPrimary}
            strokeWidth={props.extraSmallStrokeWidth}
          />
        </g>
      </g>
      {hasPlanLabel(planLabel) && (
        <PlanLabel
          text={planLabel}
          verticalAlignment={TextVerticalAlignment.TOP}
          transform={[
            translate(0, loadPlanLabelYOffset(props, TRACK_CEILING_LIGHT_SYMBOL_EXTENT_Y, planLabel)),
            mat3Scale(inverseScale, inverseScale),
            rotate(-angleCW),
          ]}
          fill={props.textFillPrimary}
          fontSize={numMul(props.smallFontSize, props.svgTextScale)}
          fontFamily={props.fontFamily0}
          alignment={TextAlignment.CENTER}
          stroke={props.textStrokePrimary}
          strokeWidth={props.textStrokeWidth}
        />
      )}
    </g>
  );
};

const CeilingFan = (props: Required<LoadProps>) => {
  const planLabel = props.loadDescriptor.planLabel;
  const scale = props.scale * SVG_UNITS_PER_100TH_INCH;
  const inverseScale = 1 / scale;
  const angleCW = keepTextUprightAngle(props);
  return (
    <g transform={`translate(${props.x} ,${props.y}) scale(${scale},${scale})`}>
      <ellipse
        cx="0"
        cy="0"
        rx="5"
        ry="5"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.extraSmallStrokeWidth}
      />
      <g transform="rotate(45)">
        <rect
          id="fan_blade"
          x="-5"
          y="8"
          width="10"
          height="40"
          rx="4"
          fill={props.fillLightPrimary}
          stroke={props.strokeDarkPrimary}
          strokeWidth={props.extraSmallStrokeWidth}
        />
      </g>
      <g transform="rotate(135)">
        <rect
          id="fan_blade"
          x="-5"
          y="8"
          width="10"
          height="40"
          rx="4"
          fill={props.fillLightPrimary}
          stroke={props.strokeDarkPrimary}
          strokeWidth={props.extraSmallStrokeWidth}
        />
      </g>
      <g transform="rotate(225)">
        <rect
          id="fan_blade"
          x="-5"
          y="8"
          width="10"
          height="40"
          rx="4"
          fill={props.fillLightPrimary}
          stroke={props.strokeDarkPrimary}
          strokeWidth={props.extraSmallStrokeWidth}
        />
      </g>
      <g transform="rotate(315)">
        <rect
          id="fan_blade"
          x="-5"
          y="8"
          width="10"
          height="40"
          rx="4"
          fill={props.fillLightPrimary}
          stroke={props.strokeDarkPrimary}
          strokeWidth={props.extraSmallStrokeWidth}
        />
      </g>
      {hasPlanLabel(planLabel) && (
        <PlanLabel
          text={planLabel}
          transform={[rotate(-angleCW), translate(15, 3), mat3Scale(inverseScale, inverseScale)]}
          fill={props.textFillPrimary}
          fontSize={numMul(props.smallFontSize, props.svgTextScale)}
          fontFamily={props.fontFamily0}
          alignment={TextAlignment.LEFT}
          stroke={props.textStrokePrimary}
          strokeWidth={props.textStrokeWidth}
        />
      )}
    </g>
  );
};

const CeilingFanLight = (props: Required<LoadProps>) => {
  const planLabel = props.loadDescriptor.planLabel;
  const scale = props.scale * SVG_UNITS_PER_100TH_INCH;
  const inverseScale = 1 / scale;
  const angleCW = keepTextUprightAngle(props);
  return (
    <g transform={`translate(${props.x} ,${props.y}) scale(${scale},${scale})`}>
      <ellipse
        cx="0"
        cy="0"
        rx="5"
        ry="5"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.extraSmallStrokeWidth}
      />
      <ellipse
        cx="0"
        cy="0"
        rx="3"
        ry="3"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.extraSmallStrokeWidth}
      />
      <g transform="rotate(45)">
        <rect
          id="fan_blade"
          x="-5"
          y="8"
          width="10"
          height="40"
          rx="4"
          fill={props.fillLightPrimary}
          stroke={props.strokeDarkPrimary}
          strokeWidth={props.extraSmallStrokeWidth}
        />
      </g>
      <g transform="rotate(135)">
        <rect
          id="fan_blade"
          x="-5"
          y="8"
          width="10"
          height="40"
          rx="4"
          fill={props.fillLightPrimary}
          stroke={props.strokeDarkPrimary}
          strokeWidth={props.extraSmallStrokeWidth}
        />
      </g>
      <g transform="rotate(225)">
        <rect
          id="fan_blade"
          x="-5"
          y="8"
          width="10"
          height="40"
          rx="4"
          fill={props.fillLightPrimary}
          stroke={props.strokeDarkPrimary}
          strokeWidth={props.extraSmallStrokeWidth}
        />
      </g>
      <g transform="rotate(315)">
        <rect
          id="fan_blade"
          x="-5"
          y="8"
          width="10"
          height="40"
          rx="4"
          fill={props.fillLightPrimary}
          stroke={props.strokeDarkPrimary}
          strokeWidth={props.extraSmallStrokeWidth}
        />
      </g>
      {hasPlanLabel(planLabel) && (
        <PlanLabel
          text={planLabel}
          transform={[rotate(-angleCW), translate(15, 3), mat3Scale(inverseScale, inverseScale)]}
          fill={props.textFillPrimary}
          fontSize={numMul(props.smallFontSize, props.svgTextScale)}
          fontFamily={props.fontFamily0}
          alignment={TextAlignment.LEFT}
          stroke={props.textStrokePrimary}
          strokeWidth={props.textStrokeWidth}
        />
      )}
    </g>
  );
};

const FanPrewire = (props: Required<LoadProps>) => {
  const planLabel = props.loadDescriptor.planLabel;
  const scale = props.scale * SVG_UNITS_PER_100TH_INCH;
  const inverseScale = 1 / scale;
  const angleCW = keepTextUprightAngle(props);
  return (
    <g transform={`translate(${props.x} ,${props.y}) scale(${scale},${scale})`}>
      <ellipse
        cx="0"
        cy="0"
        rx="5"
        ry="5"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.extraSmallStrokeWidth}
      />
      <g transform="rotate(45)">
        <line x1="0" y1="5" x2="0" y2="15" stroke={props.strokeDarkPrimary} strokeWidth={props.regularStrokeWidth} />
        <line x1="0" y1="-5" x2="0" y2="-15" stroke={props.strokeDarkPrimary} strokeWidth={props.regularStrokeWidth} />
        <line x1="5" y1="0" x2="15" y2="0" stroke={props.strokeDarkPrimary} strokeWidth={props.regularStrokeWidth} />
        <line x1="-5" y1="0" x2="-15" y2="0" stroke={props.strokeDarkPrimary} strokeWidth={props.regularStrokeWidth} />
      </g>
      {hasPlanLabel(planLabel) && (
        <PlanLabel
          text={planLabel}
          transform={[rotate(-angleCW), translate(10, 3), mat3Scale(inverseScale, inverseScale)]}
          fill={props.textFillPrimary}
          fontSize={numMul(props.smallFontSize, props.svgTextScale)}
          fontFamily={props.fontFamily0}
          alignment={TextAlignment.LEFT}
          stroke={props.textStrokePrimary}
          strokeWidth={props.textStrokeWidth}
        />
      )}
    </g>
  );
};

const BathFan = (props: Required<LoadProps>) => {
  const planLabel = props.loadDescriptor.planLabel;
  const scale = props.scale * SVG_UNITS_PER_100TH_INCH;
  const inverseScale = 1 / scale;
  const angleCW = keepTextUprightAngle(props);
  return (
    <g transform={`translate(${props.x} ,${props.y}) scale(${scale},${scale})`}>
      <rect
        x="-9"
        y="-9"
        width="18"
        height="18"
        rx="0"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.smallStrokeWidth}
      />
      <ellipse
        cx="0"
        cy="0"
        rx="7.5"
        ry="7.5"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.smallStrokeWidth}
      />
      <path
        fill={props.fillDarkPrimary}
        transform="rotate(180) translate(-4,7.6) scale(.019,-.019)"
        d="M596 431C596 88.6 329.333 1.66667 196 1C328 31 398 83 396 225C394 367 222 397 180 401C138 405 16 431 1.99998 587C-9.20002 711.8 126.667 779 196 797C318 819 596 685 596 431"
      />
      {hasPlanLabel(planLabel) && (
        <PlanLabel
          text={planLabel}
          transform={[rotate(-angleCW), translate(12, 12), mat3Scale(inverseScale, inverseScale)]}
          fill={props.textFillPrimary}
          fontSize={numMul(props.smallFontSize, props.svgTextScale)}
          fontFamily={props.fontFamily0}
          alignment={TextAlignment.LEFT}
          stroke={props.textStrokePrimary}
          strokeWidth={props.textStrokeWidth}
        />
      )}
    </g>
  );
};

const BathFanLight = (props: Required<LoadProps>) => {
  const planLabel = props.loadDescriptor.planLabel;
  const scale = props.scale * SVG_UNITS_PER_100TH_INCH;
  const inverseScale = 1 / scale;
  const angleCW = keepTextUprightAngle(props);
  return (
    <g transform={`translate(${props.x} ,${props.y}) scale(${scale},${scale})`}>
      <rect
        x="-9"
        y="-9"
        width="18"
        height="18"
        rx="0"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.smallStrokeWidth}
      />
      <line x1="-9" y1="0" x2="-13" y2="0" stroke={props.strokeDarkPrimary} strokeWidth={props.smallStrokeWidth} />
      <line x1="9" y1="0" x2="13" y2="0" stroke={props.strokeDarkPrimary} strokeWidth={props.smallStrokeWidth} />
      <line x1="0" y1="-9" x2="0" y2="-13" stroke={props.strokeDarkPrimary} strokeWidth={props.smallStrokeWidth} />
      <line x1="0" y1="9" x2="0" y2="13" stroke={props.strokeDarkPrimary} strokeWidth={props.smallStrokeWidth} />

      <ellipse
        cx="0"
        cy="0"
        rx="7.5"
        ry="7.5"
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.smallStrokeWidth}
      />
      <path
        fill={props.fillDarkPrimary}
        transform="rotate(180) translate(-4,7.6) scale(.019,-.019)"
        d="M596 431C596 88.6 329.333 1.66667 196 1C328 31 398 83 396 225C394 367 222 397 180 401C138 405 16 431 1.99998 587C-9.20002 711.8 126.667 779 196 797C318 819 596 685 596 431"
      />
      {hasPlanLabel(planLabel) && (
        <PlanLabel
          text={planLabel}
          transform={[rotate(-angleCW), translate(12, 12), mat3Scale(inverseScale, inverseScale)]}
          fill={props.textFillPrimary}
          fontSize={numMul(props.smallFontSize, props.svgTextScale)}
          fontFamily={props.fontFamily0}
          alignment={TextAlignment.LEFT}
          stroke={props.textStrokePrimary}
          strokeWidth={props.textStrokeWidth}
        />
      )}
    </g>
  );
};

/** Extent of the CommsTV jack icon along local Y, matching OUTLET_LINE_BOTTOM's role for outlets. */
const COMMS_TV_SYMBOL_EXTENT_Y = 16;

const CommsTV = (props: Required<LoadProps>) => {
  const planLabel = props.loadDescriptor.planLabel;
  const scale = props.scale * SVG_UNITS_PER_100TH_INCH;
  const inverseScale = 1 / scale;
  const angleCW = keepTextUprightAngle(props);

  return (
    <g transform={`translate(${props.x} ,${props.y}) scale(${scale},${scale}) rotate(180)`}>
      <path
        d="m-8.25,-16l8,10l8,-10"
        transform="rotate(180)"
        fill={props.fillDarkPrimary}
        strokeWidth={props.smallStrokeWidth}
        stroke={props.strokeDarkPrimary}
      />
      <line x1="-10" y1="5" x2="10" y2="5" stroke={props.strokeDarkPrimary} strokeWidth={props.highlightStrokeWidth} />
      {hasPlanLabel(planLabel) && (
        <PlanLabel
          text={planLabel}
          verticalAlignment={TextVerticalAlignment.TOP}
          transform={[
            translate(0, loadPlanLabelYOffset(props, COMMS_TV_SYMBOL_EXTENT_Y, planLabel)),
            mat3Scale(inverseScale, inverseScale),
            rotate(-angleCW + 180),
          ]}
          fill={props.textFillPrimary}
          fontSize={numMul(props.smallFontSize, props.svgTextScale)}
          fontFamily={props.fontFamily0}
          alignment={TextAlignment.CENTER}
          stroke={props.textStrokePrimary}
          strokeWidth={props.textStrokeWidth}
        />
      )}
    </g>
  );
};

const OUTLET_CIRCLE_CY = 8;
const OUTLET_CIRCLE_R = 4;
const OUTLET_LINE_TOP = 0;
const OUTLET_LINE_BOTTOM = 12;
/** Gap from symbol extent to plan label text top, in pre-scale symbol units (~sheet px at scale 1). */
const LOAD_PLAN_LABEL_GAP_SYMBOL_UNITS = 8;
/**
 * Small-symbol extent (outlet circle/line bottom) the flat gap is tuned for; symbols this size get
 * no extra standoff.
 */
const LOAD_PLAN_LABEL_REFERENCE_EXTENT = 12;
/**
 * Fraction of a symbol's excess extent added as extra standoff, so a larger symbol reads with the
 * same visual gap as a small one instead of looking crowded.
 */
const LOAD_PLAN_LABEL_GAP_EXTENT_RATIO = 0.5;
/**
 * Minimum gap kept along the text axis (non-cardinal walls). Smaller than
 * LOAD_PLAN_LABEL_GAP_SYMBOL_UNITS because that axis has no ascent/descent padding to eat into.
 */
const LOAD_PLAN_LABEL_GAP_ALONG_TEXT_SYMBOL_UNITS = 3;
/**
 * Estimated glyph advance per character, as a fraction of font size (approximates Arial Narrow).
 * Used to size the label-width estimate without measuring the actual font.
 */
const LABEL_CHAR_WIDTH_RATIO = 0.45;
/**
 * Single-line label height as a fraction of font size (Arial Narrow line height).
 * Matches the line height the prior fontkit measurement produced.
 */
const LABEL_LINE_HEIGHT_RATIO = 1.1475;

/**
 * Extra clearance, in pre-scale symbol units, so long labels don't swing back over the symbol
 * on rotated walls. As the wall rotates off horizontal, the label's anchor offset swings along
 * the text's own axis, so a center-anchored label's half-width can reach back over the symbol;
 * this scales in that extra clearance by |sin(angleCW)| (zero on cardinal walls, full at 90°).
 */
export function labelClearanceSymbolUnits(props: Required<LoadProps>, planLabel: string, angleCW: number): number {
  const alongTextFactor = Math.abs(Math.sin(degreeToRadian(angleCW)));
  if (alongTextFactor < DIST_EPSILON) {
    return 0;
  }
  const symbolScale = props.scale * SVG_UNITS_PER_100TH_INCH;
  const halfWidthSymbolUnits = estimatedLabelSheetPixels(props, planLabel).width / 2 / symbolScale;
  const gapSlackSymbolUnits =
    (LOAD_PLAN_LABEL_GAP_SYMBOL_UNITS - LOAD_PLAN_LABEL_GAP_ALONG_TEXT_SYMBOL_UNITS) / symbolScale;
  return Math.max(0, halfWidthSymbolUnits - gapSlackSymbolUnits) * alongTextFactor;
}

/**
 * Estimated label box in sheet pixels, from character count rather than font measurement.
 * Only feeds the clearance/gap offset tweaks, so an estimate avoids a font-load dependency here.
 */
function estimatedLabelSheetPixels(props: Required<LoadProps>, planLabel: string): { width: number; height: number } {
  const fontSize = numMul(props.smallFontSize, props.svgTextScale);
  return {
    width: numMul(fontSize, planLabel.length * LABEL_CHAR_WIDTH_RATIO),
    height: numMul(fontSize, LABEL_LINE_HEIGHT_RATIO),
  };
}

/**
 * The hanging-baseline label reads closer to the symbol when it lands above it (glyphs extend
 * back toward the symbol) than below (glyphs extend away). Pull the below-side anchor in by the
 * measured text height, scaled by |cos(angleCW)|, so both sides read as the same gap.
 */
export function labelBelowSymbolAdjustmentSymbolUnits(
  props: Required<LoadProps>,
  planLabel: string,
  angleCW: number,
): number {
  const belowFactor = Math.max(0, -Math.cos(degreeToRadian(angleCW)));
  if (belowFactor < DIST_EPSILON) {
    return 0;
  }
  const symbolScale = props.scale * SVG_UNITS_PER_100TH_INCH;
  return (estimatedLabelSheetPixels(props, planLabel).height / symbolScale) * belowFactor;
}

function loadPlanLabelYOffset(props: Required<LoadProps>, symbolExtentY: number, planLabel: string): number {
  const symbolScale = props.scale * SVG_UNITS_PER_100TH_INCH;
  // Scale the gap so it stays ~constant in sheet pixels when export scale changes.
  const gapSymbolUnits = LOAD_PLAN_LABEL_GAP_SYMBOL_UNITS / symbolScale;
  // Larger symbols get extra standoff so the gap reads the same as it does next to a small outlet.
  const sizeStandoff = Math.max(0, symbolExtentY - LOAD_PLAN_LABEL_REFERENCE_EXTENT) * LOAD_PLAN_LABEL_GAP_EXTENT_RATIO;
  const angleCW = keepTextUprightAngle(props);
  return (
    symbolExtentY +
    gapSymbolUnits +
    sizeStandoff +
    labelClearanceSymbolUnits(props, planLabel, angleCW) -
    labelBelowSymbolAdjustmentSymbolUnits(props, planLabel, angleCW)
  );
}
const OUTLET_DUPLEX_SLOT_XS = [-1.5, 1.5] as const;
/** Four vertical slots evenly spaced across the outlet circle diameter. */
const OUTLET_QUAD_SLOT_XS = [-2.4, -0.8, 0.8, 2.4] as const;
const OUTLET_CIRCLE_TOP = OUTLET_CIRCLE_CY - OUTLET_CIRCLE_R;

function outletStrokeProps(props: Required<LoadProps>) {
  return {
    stroke: props.strokeDarkPrimary,
    strokeWidth: props.extraSmallStrokeWidth,
  };
}

function outletSymbolLayout(props: Required<LoadProps>, options?: { rotateSymbol?: boolean }) {
  const scale = props.scale * SVG_UNITS_PER_100TH_INCH;
  const inverseScale = 1 / scale;
  const angleCW = keepTextUprightAngle(props);
  const rotateSymbol = options?.rotateSymbol ?? true;
  const scaleTransform = `translate(${props.x} ,${props.y}) scale(${scale},${scale})`;
  return {
    scale,
    inverseScale,
    angleCW,
    rotateSymbol,
    transform: rotateSymbol ? `${scaleTransform} rotate(180)` : scaleTransform,
  };
}

function OutletVerticalLines(props: Required<LoadProps>, xPositions: readonly number[]) {
  return xPositions.map((x) => (
    <line key={x} x1={x} y1={OUTLET_LINE_TOP} x2={x} y2={OUTLET_LINE_BOTTOM} {...outletStrokeProps(props)} />
  ));
}

/** Top prong segments outside the circle, on the plan-label side of the symbol after rotate(180). */
function OutletTopExteriorProngs(props: Required<LoadProps>, xPositions: readonly number[]) {
  return xPositions.map((x) => (
    <line key={x} x1={x} y1={OUTLET_LINE_TOP} x2={x} y2={OUTLET_CIRCLE_TOP} {...outletStrokeProps(props)} />
  ));
}

const OutletDuplex1 = (props: Required<LoadProps>) => {
  const layout = outletSymbolLayout(props);
  return (
    <g transform={layout.transform}>
      <OutletCircle {...props} />
      {OutletVerticalLines(props, OUTLET_DUPLEX_SLOT_XS)}
      {OutletPlanLabel(props, layout)}
    </g>
  );
};

const Outlet = OutletDuplex1;

function OutletCircle(props: Required<LoadProps>) {
  return (
    <ellipse
      cx="0"
      cy={OUTLET_CIRCLE_CY}
      rx={OUTLET_CIRCLE_R}
      ry={OUTLET_CIRCLE_R}
      fill={props.fillLightPrimary}
      stroke={props.strokeDarkPrimary}
      strokeWidth={props.smallStrokeWidth}
    />
  );
}

function pointOnOutletCircle(angleDeg: number): [number, number] {
  const rad = (angleDeg * Math.PI) / 180;
  return [OUTLET_CIRCLE_R * Math.cos(rad), OUTLET_CIRCLE_CY - OUTLET_CIRCLE_R * Math.sin(rad)];
}

function outletSpecialInscribedTrianglePath(): string {
  // Equilateral triangle inscribed in the circle with apex pointing down.
  const apexAngleDeg = 270;
  const [apexX, apexY] = pointOnOutletCircle(apexAngleDeg);
  const [upperX, upperY] = pointOnOutletCircle(apexAngleDeg + 120);
  const [lowerX, lowerY] = pointOnOutletCircle(apexAngleDeg - 120);
  return `${apexX},${apexY} ${upperX},${upperY} ${lowerX},${lowerY}`;
}

const OutletSpecial = (props: Required<LoadProps>) => {
  const layout = outletSymbolLayout(props);
  return (
    <g transform={layout.transform}>
      <OutletCircle {...props} />
      <polygon
        points={outletSpecialInscribedTrianglePath()}
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.extraSmallStrokeWidth}
      />
      {OutletTopExteriorProngs(props, OUTLET_DUPLEX_SLOT_XS)}
      {OutletPlanLabel(props, layout)}
    </g>
  );
};

function OutletPlanLabel(
  props: Required<LoadProps>,
  layout: ReturnType<typeof outletSymbolLayout>,
  symbolExtentY = OUTLET_LINE_BOTTOM,
) {
  const planLabel = props.loadDescriptor.planLabel;
  if (!hasPlanLabel(planLabel)) {
    return null;
  }

  const { inverseScale, angleCW, rotateSymbol } = layout;
  const labelYOffset = loadPlanLabelYOffset(props, symbolExtentY, planLabel);
  return (
    //  TextAlignment.CENTER allows centering on the anchor (matching the Studio editor) instead of rotation-dependent alignment,
    // which disagreed with the model-space anchor offset under a rotated viewport.
    <PlanLabel
      text={planLabel}
      verticalAlignment={TextVerticalAlignment.TOP}
      transform={[
        translate(0, labelYOffset),
        mat3Scale(inverseScale, inverseScale),
        rotate(-angleCW + (rotateSymbol ? 180 : 0)),
      ]}
      fill={props.textFillPrimary}
      fontSize={numMul(props.smallFontSize, props.svgTextScale)}
      fontFamily={props.fontFamily0}
      alignment={TextAlignment.CENTER}
      stroke={props.textStrokePrimary}
      strokeWidth={props.textStrokeWidth}
    />
  );
}

const OutletSingle = (props: Required<LoadProps>) => {
  const layout = outletSymbolLayout(props);
  return (
    <g transform={layout.transform}>
      <OutletCircle {...props} />
      {OutletVerticalLines(props, [0])}
      {OutletPlanLabel(props, layout)}
    </g>
  );
};

const OutletDuplex2 = (props: Required<LoadProps>) => {
  const layout = outletSymbolLayout(props);
  const squareTop = OUTLET_CIRCLE_CY - OUTLET_CIRCLE_R;
  const squareSize = OUTLET_CIRCLE_R * 2;
  return (
    <g transform={layout.transform}>
      <rect
        x={-OUTLET_CIRCLE_R}
        y={squareTop}
        width={squareSize}
        height={squareSize}
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.smallStrokeWidth}
      />
      {OutletVerticalLines(props, OUTLET_DUPLEX_SLOT_XS)}
      {OutletPlanLabel(props, layout)}
    </g>
  );
};

const OutletSwitched = (props: Required<LoadProps>) => {
  const layout = outletSymbolLayout(props);
  return (
    <g transform={layout.transform}>
      <OutletCircle {...props} />
      {OutletVerticalLines(props, OUTLET_DUPLEX_SLOT_XS)}
      {OutletPlanLabel(props, layout)}
    </g>
  );
};

const OutletTriplex = (props: Required<LoadProps>) => {
  const layout = outletSymbolLayout(props);
  return (
    <g transform={layout.transform}>
      <OutletCircle {...props} />
      {OutletVerticalLines(props, [-1.5, 0, 1.5])}
      {OutletPlanLabel(props, layout)}
    </g>
  );
};

/** Duplex outlet slots plus a third, evenly-spaced prong pointing the same direction, for 240V/220V loads. */
const HIGH_VOLTAGE_OUTLET_SLOT_XS = [-1.5, 0, 1.5] as const;

const OutletHighVoltage = (props: Required<LoadProps>) => {
  const layout = outletSymbolLayout(props);
  return (
    <g transform={layout.transform}>
      <OutletCircle {...props} />
      {OutletVerticalLines(props, HIGH_VOLTAGE_OUTLET_SLOT_XS)}
      {OutletPlanLabel(props, layout)}
    </g>
  );
};

const OutletQuad = (props: Required<LoadProps>) => {
  const layout = outletSymbolLayout(props);
  return (
    <g transform={layout.transform}>
      <OutletCircle {...props} />
      {OutletVerticalLines(props, OUTLET_QUAD_SLOT_XS)}
      {OutletPlanLabel(props, layout)}
    </g>
  );
};

const Square = (props: Required<LoadProps>) => {
  const layout = outletSymbolLayout(props);
  const squareSize = CAN_LIGHT_OUTER_RADIUS * 2;

  return (
    <g transform={layout.transform}>
      <rect
        x={-CAN_LIGHT_OUTER_RADIUS}
        y={OUTLET_LINE_TOP}
        width={squareSize}
        height={squareSize}
        fill={props.fillLightPrimary}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.smallStrokeWidth}
      />
      {OutletPlanLabel(props, layout, OUTLET_LINE_TOP + CAN_LIGHT_OUTER_RADIUS * 2)}
    </g>
  );
};

const TextOnly = (props: Required<LoadProps>) => {
  const planLabel = props.loadDescriptor.planLabel;
  if (!hasPlanLabel(planLabel)) {
    return null;
  }

  const scale = props.scale * SVG_UNITS_PER_100TH_INCH;
  const inverseScale = 1 / scale;
  const angleCW = keepTextUprightAngle(props);
  const textAlign = alignTextForRotation(angleCW);
  return (
    <g transform={`translate(${props.x} ,${props.y}) scale(${scale},${scale})`}>
      <PlanLabel
        text={planLabel}
        transform={[rotate(-angleCW), translate(0, 3.5), mat3Scale(inverseScale, inverseScale)]}
        fill={props.textFillPrimary}
        fontSize={numMul(props.smallFontSize, props.svgTextScale)}
        fontFamily={props.fontFamily0}
        alignment={textAlign}
        stroke={props.textStrokePrimary}
        strokeWidth={props.textStrokeWidth}
      />
    </g>
  );
};

const FloorToFloorTransition = (props: Required<LoadProps>) => {
  const scale = props.scale * SVG_UNITS_PER_100TH_INCH;
  const { floorIndex } = props;
  const inverseScale = 1 / scale;
  const angleCW = keepTextUprightAngle(props);
  const textAlign = alignTextForRotation(angleCW);
  const verticalOffset = props.posFromController ? -10 : 0;
  return (
    <g transform={`translate(${props.x} ,${props.y}) scale(${scale},${scale}) rotate(180)`}>
      <PlanLabel
        text={"TO FLR " + (floorIndex + 1)}
        transform={[
          translate(0, 0),
          mat3Scale(inverseScale, inverseScale),
          rotate(-angleCW + 180),
          translate(0, 4.5 + verticalOffset),
        ]}
        fill={props.textFillPrimary}
        fontSize={numMul(props.smallFontSize, props.svgTextScale)}
        fontFamily={props.fontFamily0}
        alignment={textAlign}
        stroke={props.textStrokePrimary}
        strokeWidth={props.textStrokeWidth}
      />
    </g>
  );
};

export const loadSymbolSelector: { [key in LoadSymbol]: any } = {
  NONE: null,

  //single line circle
  CIRCLE_4: Circle4,
  CIRCLE_7: Circle7,
  CIRCLE_8: Circle8,
  FRAMED_CIRCLE: FramedCircle,

  //double line circle
  CIRCLE_DL_45_15: CircleDL4515,
  CIRCLE_DL_60_40: CircleDL6040,
  CIRCLE_DL_12: CircleDL12,

  //double line rectangle
  RECTANGLE_DL_2X4: RectangleDL2X4,
  RECTANGLE_DL_1X4: RectangleDL1X4,
  RECTANGLE_DL_24X4: RectangleDL24X4,

  //specific symbols
  CEILING_LIGHT: CeilingLight,
  MOUNTED_WALL_LIGHT: MountedWallLight,
  FLOOD_LIGHT: FloodLight,
  TRACK_CEILING_LIGHT: TrackCeilingLight,

  CEILING_FAN: CeilingFan,
  CEILING_FAN_LIGHT: CeilingFanLight,
  FAN_PREWIRE: FanPrewire,
  BATH_FAN: BathFan,
  BATH_FAN_LIGHT: BathFanLight,

  COMMS_TV: CommsTV,

  OUTLET: Outlet,
  OUTLET_SINGLE: OutletSingle,
  OUTLET_DUPLEX_2: OutletDuplex2,
  OUTLET_SWITCHED: OutletSwitched,
  OUTLET_TRIPLEX: OutletTriplex,
  OUTLET_QUAD: OutletQuad,
  OUTLET_SPECIAL: OutletSpecial,
  HIGH_VOLTAGE_OUTLET: OutletHighVoltage,
  SQUARE: Square,
  TEXT_ONLY: TextOnly,

  FLOOR_TO_FLOOR_TRANSITION: FloorToFloorTransition,
};

function alignTextForRotation(angleCW: number): TextAlignment {
  let textAlign = TextAlignment.LEFT;

  if ((angleCW >= 0 && angleCW < 45) || (angleCW >= 330 && angleCW <= 360)) {
    textAlign = TextAlignment.CENTER;
  } else if (angleCW >= 45 && angleCW < 160) {
    textAlign = TextAlignment.LEFT;
  } else if (angleCW >= 160 && angleCW < 210) {
    textAlign = TextAlignment.CENTER;
  } else if (angleCW >= 210 && angleCW < 330) {
    textAlign = TextAlignment.RIGHT;
  }

  return textAlign;
}

function keepTextUprightAngle(props: LoadProps) {
  const [rawX, rawY] = props.normal;
  const [oX, oY] = vec2Normalize(rawX, rawY);
  const angleCW = radianToDegree(vec2AngleBetweenCW(0, 1, oX, oY) + (props.planRotationRads || 0));
  return angleCW;
}

function isJsxElement(child: unknown): child is JSX.Element {
  return typeof child === "object" && child !== null && "type" in child;
}

function loadSymbolGroupHasRotate180(symbolRoot: JSX.Element): boolean {
  const transform = symbolRoot.props?.transform;
  return typeof transform === "string" && transform.includes("rotate(180)");
}

/** World-space plan-label rotation from the full load SVG transform chain (for tests). */
export function loadPlanLabelSvgTextRotationRads(props: Required<LoadProps>): number | null {
  if (!hasPlanLabel(props.loadDescriptor.planLabel)) {
    return null;
  }

  const symbolRenderer = loadSymbolSelector[props.loadDescriptor.symbol];
  if (!symbolRenderer) {
    return null;
  }

  const loadElement = symbolRenderer(props);
  const planLabelElement = findPlanLabelInElement(loadElement);
  if (!planLabelElement?.props.transform) {
    return null;
  }

  const scale = props.scale * SVG_UNITS_PER_100TH_INCH;
  const [oX, oY] = vec2Normalize(props.normal[0], props.normal[1]);
  const wallAngleDeg = radianToDegree(vec2AngleBetweenCW(0, 1, oX, oY));

  const transform = mat3Reduce([
    rotate(wallAngleDeg, props.x, props.y),
    translate(props.x, props.y),
    mat3Scale(scale, scale),
    ...(loadSymbolGroupHasRotate180(loadElement) ? [rotate(180)] : []),
    ...(planLabelElement.props.transform as Mat3[]),
  ]);
  const scales = mat3ExtractScaleVectorFrom(transform);

  return mat3ExtractRotationAngleFrom(transform, scales) ?? 0;
}

/** Wall-aligned offset from the load anchor to the plan label center, matching the Load component's render of the real normal. */
export function loadPlanLabelLocalOffset(props: Required<LoadProps>): Vec2 | null {
  if (!hasPlanLabel(props.loadDescriptor.planLabel)) {
    return null;
  }

  const symbolRenderer = loadSymbolSelector[props.loadDescriptor.symbol];
  if (!symbolRenderer) {
    return null;
  }

  const loadElement = symbolRenderer(props);
  const planLabelElement = findPlanLabelInElement(loadElement);
  if (!planLabelElement?.props.transform) {
    return null;
  }

  const scale = props.scale * SVG_UNITS_PER_100TH_INCH;
  const [oX, oY] = vec2Normalize(props.normal[0], props.normal[1]);
  const wallAngleDeg = radianToDegree(vec2AngleBetweenCW(0, 1, oX, oY));
  const innerGroupTransform = mat3Reduce([
    rotate(wallAngleDeg, props.x, props.y),
    translate(props.x, props.y),
    mat3Scale(scale, scale),
    ...(loadSymbolGroupHasRotate180(loadElement) ? [rotate(180)] : []),
  ]);
  const planLabelTransform = mat3Reduce(planLabelElement.props.transform as Mat3[]);
  const worldPosition = mat3Dot(mat3Mult(innerGroupTransform, planLabelTransform), 0, 0);

  return [worldPosition[0] - props.x, worldPosition[1] - props.y];
}

/**
 * Symbols whose plan label reads as centered inside the glyph rather than offset outside it. The
 * SVG label anchors its text baseline below the symbol center so the glyphs land centered in the
 * circle, but the editor anchors from the label's bounding box, so it needs the symbol center plus
 * center anchoring to match. Only true of the legacy offset — under Overlap Reduction these two
 * render outside the glyph like every other round light, so the gate below opts out there.
 */
const symbolsWithLabelCenteredInGlyph: ReadonlySet<LoadSymbol> = new Set([LoadSymbol.CIRCLE_7, LoadSymbol.CIRCLE_8]);

/** Editor offset/rotation for load plan labels, matching SVGLoader path scaling in the Studio viewer. */
export function loadPlanLabelEditorPlacement(props: {
  loadDescriptor: LoadDescriptor;
  normal: Vec2;
  scale?: number;
  planRotationRads?: number;
  svgTextScale?: number;
  x?: number;
  y?: number;
  reduceOverlapLoadLabels?: boolean;
}): {
  /** World inches at the editor viewer's own fixed SVG scale. */
  offset: Vec2;
  /** The same offset in raw SVG units, for consumers that scale it themselves. */
  svgOffset: Vec2;
  uprightAngleRads: number;
  text: string;
  fontSize: number;
  verticalAnchor: PlacementYAnchor;
} | null {
  const placementProps: Required<LoadProps> = {
    ...loadDefault,
    ...props,
  };

  const localOffset = loadPlanLabelLocalOffset(placementProps);
  if (!localOffset) {
    return null;
  }

  const symbolRenderer = loadSymbolSelector[placementProps.loadDescriptor.symbol];
  if (!symbolRenderer) {
    return null;
  }

  const textInfo = findLoadTextInElement(symbolRenderer({ ...placementProps, normal: [1, 0] }));
  if (!textInfo) {
    return null;
  }

  const [localX, localY] = localOffset;
  const editorSvgScale = HUNDRETH_INCH_PER_SVG_UNIT / 2;
  const uprightAngleRads = loadPlanLabelSvgTextRotationRads(placementProps);
  if (uprightAngleRads === null) {
    return null;
  }

  if (
    !placementProps.reduceOverlapLoadLabels &&
    symbolsWithLabelCenteredInGlyph.has(placementProps.loadDescriptor.symbol)
  ) {
    return {
      ...textInfo,
      offset: [0, 0],
      svgOffset: [0, 0],
      uprightAngleRads,
      verticalAnchor: PlacementYAnchor.CENTER,
    };
  }

  return {
    ...textInfo,
    offset: [localX * editorSvgScale, -localY * editorSvgScale],
    svgOffset: [localX, -localY],
    uprightAngleRads,
    verticalAnchor: PlacementYAnchor.TOP,
  };
}

/** React unrolls a component that renders a component; this walker unrolls it by hand, so bound the
 * depth rather than trust a hand-built tree to terminate. Load symbols nest one level at most. */
const MAX_PLAN_LABEL_COMPONENT_DEPTH = 8;

/**
 * The element's own render function when it is a function component (`<RoundCeilingLightPlanLabel />`)
 * rather than an intrinsic SVG tag (`<g>`). Class components, memo and forwardRef are out of scope:
 * nothing in the load symbol trees uses them, and calling a class component without `new` throws.
 */
function elementComponentRender(element: JSX.Element): ((componentProps: unknown) => unknown) | null {
  if (typeof element.type !== "function") {
    return null;
  }
  return element.type as (componentProps: unknown) => unknown;
}

/**
 * The PlanLabel element inside a load symbol's JSX, found without a React renderer: the placement
 * helpers read transform/text/alignment straight off the element's props, so it has to come back
 * un-rendered. Hence the identity check before any expansion — PlanLabel is itself a function
 * component, and expanding it would hand back its inner text pair instead.
 *
 * Function-component wrappers are expanded by calling them, so a label rendered as
 * `<RoundCeilingLightPlanLabel loadProps={props} />` is still found. Without that the walker
 * dead-ends on the wrapper (it carries no children) and returns null, which every caller reads as
 * "this load has no label" — dropping it from sheet and editor geometry with no error.
 */
export function findPlanLabelInElement(element: JSX.Element): JSX.Element | null {
  return findPlanLabelInElementAtDepth(element, 0);
}

function findPlanLabelInElementAtDepth(element: JSX.Element, componentDepth: number): JSX.Element | null {
  if (!isJsxElement(element)) {
    return null;
  }
  if (element.type === PlanLabel) {
    return element;
  }

  const render = elementComponentRender(element);
  if (render) {
    if (componentDepth >= MAX_PLAN_LABEL_COMPONENT_DEPTH) {
      return null;
    }
    return findPlanLabelInNode(render(element.props), componentDepth + 1);
  }

  return findPlanLabelInNode(element.props?.children, componentDepth);
}

/** One `children` value or component return: an element, a nullish/boolean branch, or a nested array. */
function findPlanLabelInNode(node: unknown, componentDepth: number): JSX.Element | null {
  if (Array.isArray(node)) {
    for (const child of node as unknown[]) {
      const planLabel = findPlanLabelInNode(child, componentDepth);
      if (planLabel) {
        return planLabel;
      }
    }
    return null;
  }
  if (!isJsxElement(node)) {
    return null;
  }
  return findPlanLabelInElementAtDepth(node, componentDepth);
}

export function findLoadTextInElement(element: JSX.Element): { text: string; fontSize: number } | null {
  const planLabel = findPlanLabelInElement(element);
  if (!planLabel) {
    return null;
  }
  return {
    text: planLabel.props.text,
    fontSize: planLabel.props.fontSize,
  };
}

/**
 * Horizontal alignment actually rendered by a symbol's <PlanLabel> — varies per symbol (some are
 * TextAlignment.LEFT, others CENTER) and neither findLoadTextInElement nor
 * loadPlanLabelEditorPlacement expose it. Defaults to PlanLabel's own prop default so a symbol
 * that doesn't override it still reports the value it actually renders with.
 */
function findLoadPlanLabelAlignmentInElement(element: JSX.Element): TextAlignment | null {
  const planLabel = findPlanLabelInElement(element);
  if (!planLabel) {
    return null;
  }
  return planLabel.props.alignment ?? TextAlignment.LEFT;
}

/**
 * Sheet/PDF-space placement for a load's plan label. Reuses loadPlanLabelEditorPlacement as-is
 * for offset/rotation/text AND for placementYAnchor (its verticalAnchor) — that function
 * already resolves the general "anchor from the top" case vs. the CIRCLE_7/CIRCLE_8
 * "centered in the glyph" special case, both already validated against the real SVG output by
 * its own test suite, so there's no reason to re-derive that here.
 *
 * The one thing it doesn't expose is horizontal alignment, which does vary per symbol (some are
 * TextAlignment.LEFT, others CENTER) — read directly off the rendered <PlanLabel> and mapped to
 * placementXAnchor, so the text box anchors the same edge the SVG's text-anchor would instead of
 * defaulting to a box-centered anchor that doesn't match the baked SVG's true anchor point.
 *
 * Deliberately does not report the SVG's font size: the sheet geometry leaves fontSize undefined so
 * the style cascade owns it (see makeSheetGeometryFromElectricalLoadLabels). That also drops the
 * deprecated svgTextScale multiplier, which no longer applies to SheetGeometryType.TEXT. The offsets
 * are unaffected — loadPlanLabelLocalOffset reduces hardcoded per-symbol transforms, not text metrics.
 */
export function loadPlanLabelSheetPlacement(props: {
  loadDescriptor: LoadDescriptor;
  normal: Vec2;
  scale?: number;
  planRotationRads?: number;
  reduceOverlapLoadLabels?: boolean;
}): {
  /** Sheet inches from the load's position to the label's origin - the generator has no view
   * scale, so sheetGeometryScaleStyle converts this at render time. */
  sheetOffset: [SheetInches, SheetInches];
  uprightAngleRads: number;
  text: string;
  placementXAnchor: PlacementXAnchor;
  placementYAnchor: PlacementYAnchor;
} | null {
  const editorPlacement = loadPlanLabelEditorPlacement(props);
  if (!editorPlacement) {
    return null;
  }

  const placementProps: Required<LoadProps> = {
    ...loadDefault,
    ...props,
  };
  const symbolRenderer = loadSymbolSelector[placementProps.loadDescriptor.symbol];
  if (!symbolRenderer) {
    return null;
  }

  // Render at the caller's actual normal: TextOnly and FloorToFloorTransition derive alignment from
  // rotation, so a fixed [1, 0] reports an alignment those symbols never use.
  const alignment = findLoadPlanLabelAlignmentInElement(symbolRenderer(placementProps));
  if (alignment === null) {
    return null;
  }

  // The SVG is drawn at SVG_UNITS_TO_INCHES per unit of sheet space. The editor placement's own
  // `offset` is right for the editor's fixed SVG scale but implies 1/50 here rather than 1/48.
  const [svgOffsetX, svgOffsetY] = editorPlacement.svgOffset;
  return {
    sheetOffset: [sheetInchesNew(svgOffsetX * SVG_UNITS_TO_INCHES), sheetInchesNew(svgOffsetY * SVG_UNITS_TO_INCHES)],
    uprightAngleRads: editorPlacement.uprightAngleRads,
    text: editorPlacement.text,
    placementXAnchor: textAlignmentToXAnchor(alignment) ?? PlacementXAnchor.LEFT,
    placementYAnchor: editorPlacement.verticalAnchor,
  };
}
